import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { fmtDate, fmtTime, numWords, fmtQty, COMPANY } from './format';
import { LOGO_DATA_URL } from '../assets/logo';

/**
 * Generates a PDF for the given invoice (matching the printable invoice
 * layout from the original prototype) and triggers a download.
 * @param {object} invoice - invoice object with items array
 */
export function downloadInvoicePdf(invoice) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = 50;

  // --- Header: centered logo + email, invoice title on the right ---
  const logoHeight = 117;
  const logoWidth = logoHeight * (1179 / 906); // preserve the image's aspect ratio
  const logoX = (pageWidth - logoWidth) / 2;
  doc.addImage(LOGO_DATA_URL, 'JPEG', logoX, y - 40, logoWidth, logoHeight);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(97, 97, 97);
  doc.text(COMPANY.email, pageWidth / 2, y - 40 + logoHeight + 14, { align: 'center' });

  // Tracked, all-caps title. jsPDF only bundles Helvetica/Times/Courier, so we
  // emulate the Poppins look used in the on-screen/print views with a heavy
  // weight and light letter spacing. Right-align by computing the width manually,
  // since charSpace throws off jsPDF's built-in align math.
  const title = 'INVOICE';
  const titleSize = 32;
  const titleCharSpace = 1;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(titleSize);
  doc.setTextColor(46, 125, 50);
  const titleWidth = doc.getTextWidth(title) + titleCharSpace * (title.length - 1);
  doc.text(title, pageWidth - margin - titleWidth, y, { charSpace: titleCharSpace });

  doc.setFontSize(11);
  doc.setTextColor(26, 35, 126);
  doc.text(`# ${invoice.invoice_number}`, pageWidth - margin, y + 16, { align: 'right' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(97, 97, 97);
  doc.text(`Date: ${fmtDate(invoice.invoice_date)}`, pageWidth - margin, y + 28, { align: 'right' });
  doc.text(`Time: ${fmtTime(invoice.invoice_date)}`, pageWidth - margin, y + 40, { align: 'right' });

  y += 100;

  // --- Bill To / Bill From box ---
  doc.setFillColor(232, 245, 233); // green light
  doc.roundedRect(margin, y, pageWidth - margin * 2, 70, 4, 4, 'F');

  const colWidth = (pageWidth - margin * 2) / 2;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(46, 125, 50);
  doc.text('BILL TO', margin + 12, y + 16);
  doc.text('BILL FROM', margin + colWidth + 12, y + 16);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(26, 35, 126);
  doc.text(invoice.customer_name || 'Walk-in Customer', margin + 12, y + 30);
  doc.text(COMPANY.name, margin + colWidth + 12, y + 30);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(97, 97, 97);
  let leftLine = y + 42;
  if (invoice.customer_address) {
    doc.text(invoice.customer_address, margin + 12, leftLine, { maxWidth: colWidth - 24 });
    leftLine += 11;
  }
  if (invoice.customer_phone) {
    doc.text(`Phone: ${invoice.customer_phone}`, margin + 12, leftLine);
  }

  let rightLine = y + 42;
  doc.text(COMPANY.address, margin + colWidth + 12, rightLine);
  rightLine += 11;
  doc.text(COMPANY.city, margin + colWidth + 12, rightLine);
  rightLine += 11;
  doc.text(`Phone: ${COMPANY.phone}`, margin + colWidth + 12, rightLine);
  rightLine += 11;
  // doc.text(`GST: ${invoice.gst_number || '\u2014'}`, margin + colWidth + 12, rightLine);

  y += 85;

  // --- Meta row: Order Date / Time / By ---
  const metaCellWidth = (pageWidth - margin * 2) / 3;
  doc.setDrawColor(46, 125, 50);
  doc.roundedRect(margin, y, pageWidth - margin * 2, 36, 4, 4);
  for (let i = 1; i < 3; i++) {
    doc.line(margin + metaCellWidth * i, y, margin + metaCellWidth * i, y + 36);
  }

  const metaLabels = ['ORDER DATE', 'ORDER TIME', 'ORDER BY'];
  const metaValues = [
    fmtDate(invoice.invoice_date),
    fmtTime(invoice.invoice_date),
    invoice.customer_name || 'Walk-in',
  ];
  metaLabels.forEach((label, i) => {
    const cellX = margin + metaCellWidth * i + 10;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(46, 125, 50);
    doc.text(label, cellX, y + 13);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(26, 35, 126);
    doc.text(String(metaValues[i]), cellX, y + 26, { maxWidth: metaCellWidth - 16 });
  });

  y += 50;

  // --- Items table ---
  const rows = (invoice.items || []).map((it, idx) => [
    idx + 1,
    it.product_name || it.name,
    `Rs. ${parseFloat(it.mrp).toFixed(2)}`,
    it.base_pack ?? it.basePack ?? '',
    fmtQty(it.qty, it.unit),
    `Rs. ${parseFloat(it.price).toFixed(2)}`,
    `Rs. ${parseFloat(it.line_total ?? it.total).toFixed(2)}`,
  ]);

  doc.autoTable({
    startY: y,
    margin: { left: margin, right: margin },
    head: [['#', 'Item', 'MRP', 'Base Pack', 'Qty', 'Price', 'Total']],
    body: rows,
    styles: { fontSize: 8.5, cellPadding: 5 },
    headStyles: { fillColor: [46, 125, 50], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [250, 250, 250] },
  });

  y = doc.lastAutoTable.finalY + 20;

  // Page-break safety
  if (y > doc.internal.pageSize.getHeight() - 160) {
    doc.addPage();
    y = 50;
  }

  // --- Totals box (right aligned) ---
  const totalsBoxWidth = 220;
  const totalsX = pageWidth - margin - totalsBoxWidth;
  const subtotal = parseFloat(invoice.subtotal || 0);
  const discount = parseFloat(invoice.discount || 0);
  const tax = parseFloat(invoice.tax || 0);
  const total = parseFloat(invoice.total || 0);

  const taxRateLabel = invoice.tax_rate ? ` ${parseFloat(invoice.tax_rate).toFixed(2)}%` : '';
  const totalRows = [
    ['Sub Total', `Rs. ${subtotal.toFixed(2)}`],
    ['Discount', `Rs. ${discount.toFixed(2)}`],
    [`Tax (GST${taxRateLabel})`, `Rs. ${tax.toFixed(2)}`],
  ];

  let ty = y;
  doc.setFontSize(9);
  totalRows.forEach(([label, val], idx) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(33, 33, 33);
    doc.text(label, totalsX, ty);
    doc.text(val, totalsX + totalsBoxWidth, ty, { align: 'right' });
    if (idx < totalRows.length - 1) {
      doc.setDrawColor(238, 238, 238);
      doc.line(totalsX, ty + 4, totalsX + totalsBoxWidth, ty + 4);
    }
    ty += 16;
  });
  ty += 6;

  doc.setFillColor(46, 125, 50);
  doc.roundedRect(totalsX, ty, totalsBoxWidth, 26, 3, 3, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Grand Total', totalsX + 10, ty + 17);
  doc.text(`Rs. ${total.toFixed(2)}`, totalsX + totalsBoxWidth - 10, ty + 17, { align: 'right' });

  // --- Amount in words + thank you (left side) ---
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(46, 125, 50);
  doc.text('TOTAL AMOUNT IN WORDS', margin, y);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8.5);
  doc.setTextColor(97, 97, 97);
  doc.text(numWords(total), margin, y + 14, { maxWidth: totalsX - margin - 20 });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(46, 125, 50);
  doc.text('THANK YOU FOR SHOPPING WITH EASY1BASKET!', margin, y + 40, { maxWidth: totalsX - margin - 20 });

  // Pin the footer to the bottom of the page, unless the content already
  // extends past that point (long invoices), in which case it follows content.
  const pageHeight = doc.internal.pageSize.getHeight();
  let footerY = Math.max(ty + 50, pageHeight - 50);

  // --- Footer ---
  doc.setDrawColor(224, 224, 224);
  doc.setLineDashPattern([2, 2], 0);
  doc.line(margin, footerY, pageWidth - margin, footerY);
  doc.setLineDashPattern([], 0);

  footerY += 16;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(158, 158, 158);
  doc.text(
    `${COMPANY.name} | ${COMPANY.address}, ${COMPANY.city} | Phone: ${COMPANY.phone} | GST: ${invoice.gst_number || '\u2014'}`,
    pageWidth / 2,
    footerY,
    { align: 'center' }
  );

  doc.save(`${invoice.invoice_number}.pdf`);
}
